import { ClientContractor } from '@numeo/types-core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ScopedRequestSuperseded } from 'utils/scoped-request-cache'
import { contractorApiClient, invalidateOwnerDirectory } from '../contractors/utils/api'
import { useBoxTruckProfilesMode } from '@/features/box-truck-loads/hooks/useBoxTruckProfilesMode'
import { useDriverRosterScope } from './useDriverRoster'

export const useContractors = () => {
    // In Box Truck Profiles this table is Owners, where a recruiter sees the carriers behind their own
    // drivers. Elsewhere the same hook keeps feeding the generic TMS contractor list.
    const boxTruckProfilesMode = useBoxTruckProfilesMode()
    // Keys the shared in-flight read, so an in-tab company switch cannot hand this tenant the previous one's owners.
    const scope = useDriverRosterScope()
    const [contractors, setContractors] = useState<ClientContractor[]>([])
    const [isLoading, setIsLoading] = useState(false)
    // Kept so a caller that cannot work without the list (e.g. the fuel/toll import, which would match
    // no contractor) can tell "no contractors" from "the list failed to load".
    const [error, setError] = useState<string | null>(null)

    // Dropped during the render that notices the switch, not in an effect afterwards: a company switch does
    // not remount these screens, so holding the previous tenant's owners renders them on the new page.
    const [answeredFor, setAnsweredFor] = useState(scope)
    if (answeredFor !== scope) {
        setAnsweredFor(scope)
        setContractors([])
        setError(null)
    }

    /** The identity the operator is on NOW. What an answer carries is measured against this, not itself. */
    const currentScope = useRef(scope)
    currentScope.current = scope

    /** Resolves with the rows, so a caller acting right after a save need not wait for a re-render.
     *  `fresh` drops the shared read first, or `refetch` joins a read that started before the save. */
    const load = useCallback(
        async (fresh: boolean): Promise<ClientContractor[] | undefined> => {
            if (fresh) invalidateOwnerDirectory()
            const requestScope = scope
            // A slower answer for the company the operator LEFT must not land on the one they are on. The
            // cache refuses it on the box-truck path; the TMS path reads straight from axios, so guard here.
            const stillCurrent = () => requestScope === currentScope.current
            try {
                setIsLoading(true)
                setError(null)
                const data = await contractorApiClient.fetchContractors(boxTruckProfilesMode, undefined, scope)
                if (!stillCurrent()) return undefined
                setContractors(data)
                return data
            } catch (error) {
                // Not a failure: the identity changed, or a save invalidated a read already in flight.
                if (error instanceof ScopedRequestSuperseded) return undefined
                // Not reported here: this runs per WAITER, and the Vehicles page mounts this hook four
                // times on one shared read — four toasts for one failure. The cache's `onFailure` fires
                // once per request, which is where it is said.
                console.error('Error fetching contractors:', error)
                if (stillCurrent()) setError(error instanceof Error ? error.message : 'Failed to load contractors')
                return undefined
            } finally {
                // Only the current read owns the spinner; a superseded one would clear it mid-flight.
                if (stillCurrent()) setIsLoading(false)
            }
        },
        [boxTruckProfilesMode, scope]
    )

    const fetchContractors = useCallback(() => load(false), [load])
    const refetch = useCallback(() => load(true), [load])

    useEffect(() => {
        void fetchContractors()
    }, [fetchContractors])

    return { contractors, isLoading, error, refetch, setContractors }
}
